import { Logger } from "common/utility/logger/logger";
import { TelemetryDeliveryMethod, TelemetrySchemaType, TelemetrySemanticType, TelemetryType, TelemetryUnitOfMeasure } from "../enum";
import { ILtmiBody, ILtmiTelemetry } from "../interface";

export class Ltmi {

    public static toObject(ltmiCollection: Array<any>): any {
        const returnValue: any = {}
        ltmiCollection.forEach(ltmi => {
            const deviceId: string = ltmi.deviceId
            delete ltmi.deviceId
            delete ltmi.id
            returnValue[deviceId] = ltmi;
        })
        return returnValue
    }

    public static build(ltmiRaw: Array<any>): Array<any> {
        const returnValue: Array<any> = []
        ltmiRaw.forEach(ltmi => {
            const node: ILtmiBody = {
                deviceId: ltmi['@id'],
                displayName: ltmi.displayName,
                children: Ltmi.parseLtmiNode(ltmi),
                dtcMapping: Ltmi.getDTCMapping(ltmi.dtcMapping)
            }
            returnValue.push(node)
        })
        return returnValue

    } // buildLTMI()  

    private static parseLtmiNode(ltmi: any): any {
        let telemetryList: any = {}
        ltmi.contents.forEach((item: any) => {
            const type = Ltmi.getTelemetryType(item['@type']);
            if (type === undefined) {
                Logger.error(`Definition-Worker parseLtmiNode LTMI Signal Discarded - Undefined getTelemetryType: ${item}`)
                return
            }
            telemetryList[item.name] = Ltmi.processLtmiTelemetry(item, ltmi);
        })

        Ltmi.applyDeliveryMethod(telemetryList, ltmi.webLogging)

        return telemetryList;
    } // parseLtmiNode()    

    private static getDTCMapping(deviceDTC: any): any | undefined {
        let dtcList: any = {}
        if (!deviceDTC)
            return undefined

        deviceDTC.forEach((dtc: any) => {
            dtcList[dtc.code] = {
                displayName: dtc.displayName
            }
        });

        return dtcList
    } // getDTCMapping()

    private static getTelemetryType(type: Array<string> | string): TelemetryType | undefined {

        if (type == undefined) {
            return undefined
        }

        try {
            // Single Entry
            if (!Array.isArray(type)) {
                const inlist = Object.values(TelemetryType).find((val: string) => {
                    if (val.toLowerCase() === type.toLowerCase())
                        return val;
                    return undefined;
                });
                return (inlist !== undefined) ? TelemetryType[inlist] : undefined;
            }

            // Iterate and check
            let found: TelemetryType | undefined = undefined;
            type.forEach(item => {
                if (found === undefined) {
                    found = Object.values(TelemetryType).find((val: string) => {
                        if (val.toLowerCase() === item.toLowerCase())
                            return val;
                        return undefined;
                    });
                }
            });

            return (found !== undefined) ? TelemetryType[found] : undefined;

        } catch (error) {
            Logger.error(`Definition-Worker getTelemetryType: ${type} ${error}`)
        }

        return undefined
    } // getTelemetryType()      

    private static processLtmiTelemetry(item: any, ltmi: any): ILtmiTelemetry | undefined {
        let telem: ILtmiTelemetry | undefined

        const type = Ltmi.getTelemetryType(item['@type']);
        if (type === undefined) {
            Logger.error(`Definition-Worker processLtmiTelemetry LTMI Signal Discarded - Undefined getTelemetryType: ${item} ${ltmi}`)
            return undefined
        }

        if (type === TelemetryType.Array) {
            telem = {
                displayName: item.displayName,
                type: type,
                deliveryMethod: undefined,
                schemaType: undefined,
                complexSchema: Ltmi.getComplexSchema(type, item.schema, ltmi),
                semanticType: undefined,
                unit: undefined
            }
            return telem
        } // TELEMETRY_TYPE.Array

        let schema = Ltmi.getSchemaType(item.schema)
        if (schema === undefined) {
            Logger.error(`Definition-Worker processLtmiTelemetry LTMI Signal Discarded - Unknown getSchemaType: ${item}`)
            return undefined
        }

        telem = {
            displayName: item.displayName,
            type: type,
            deliveryMethod: undefined,
            schemaType: schema,
            complexSchema: Ltmi.getComplexSchema(type, item, ltmi),
            semanticType: Ltmi.getSemanticType(item['@type']), // Semantic & unit should exist
            unit: Ltmi.getUoM(item.unit),
            defaults: item.defaults
        }

        return telem
    } // processLtmiTelemetry()  

    private static getComplexSchema(parentType: TelemetryType | undefined, item: any, ltmi: any): any | undefined {
        let objectArray: any = {}

        // Determine Complex Schema Type
        const schemaTelemetryType = Ltmi.getTelemetryType(item['@type'])
        if (schemaTelemetryType === undefined) {
            Logger.error(`Definition-Worker getComplexSchema No @Type: ${item}`)
            return undefined
        }

        if (parentType === TelemetryType.Array) {

            if (schemaTelemetryType === TelemetryType.Object) {
                const telem: ILtmiTelemetry = {
                    displayName: item.displayName,
                    type: TelemetryType.Object,
                    deliveryMethod: undefined,
                    schemaType: undefined,
                    complexSchema: Ltmi.getObjectSchema(item, ltmi),
                    semanticType: undefined,
                    unit: undefined
                }
                return telem
            } else {

                const telem: ILtmiTelemetry = {
                    displayName: item.displayName,
                    type: Ltmi.getTelemetryType(item['@type']),
                    deliveryMethod: undefined,
                    schemaType: Ltmi.getSchemaType(item.schema),
                    complexSchema: undefined,
                    semanticType: Ltmi.getSemanticType(item['@type']),
                    unit: Ltmi.getUoM(item.unit),
                    defaults: item.defaults
                }
                return telem
            }
        } // if parentType = Type Array

        // Process Non-Array Complex types
        const schemaType = Ltmi.getSchemaType(item.schema)
        switch (schemaType) {
            case TelemetrySchemaType.Enum:
                return Ltmi.getEnumSchema(item.schema)
        }

        return undefined
    } // getComplexSchema()

    private static applyDeliveryMethod(ltmi: any, logging: Array<any> | undefined): void {
        if (ltmi == undefined || logging == undefined)
            return

        if (!Array.isArray(logging)) {
            Logger.error(`LTMI Missing Logging: ${ltmi}`)
            return
        }

        logging.forEach((log: any) => {
            const keys = Object.keys(log)
            if (keys == undefined) {
                return
            }

            // Array of Items?
            if (ltmi[keys[0]].type == TelemetryType.Array && ![TelemetryType.Array, TelemetryType.Object].includes(ltmi[keys[0]].complexSchema.type)) {
                ltmi[keys[0]].complexSchema.deliveryMethod = log[keys[0]].stream ? TelemetryDeliveryMethod.Streaming : TelemetryDeliveryMethod.Bulk
            } else
                // Array of Object?
                if (ltmi[keys[0]].type == TelemetryType.Array && [TelemetryType.Object].includes(ltmi[keys[0]].complexSchema.type)) {
                    Ltmi.applyDeliveryMethod(ltmi[keys[0]].complexSchema.complexSchema, log[keys[0]])
                } else {
                    ltmi[keys[0]].deliveryMethod = log[keys[0]].stream ? TelemetryDeliveryMethod.Streaming : TelemetryDeliveryMethod.Bulk
                }
        })

    } // applyDeliveryMethod()

    private static getSchemaType(schema: Array<any> | any): TelemetrySchemaType | undefined {

        try {
            // Telemetry Schema
            if (typeof (schema) == 'string' || Array.isArray(schema)) {
                let schemaArray: Array<string> = []
                if (typeof (schema) == 'string') {
                    schemaArray = [schema]
                }
                else
                    schemaArray = schema
                for (let i = 0; i < schemaArray.length; i++) {
                    const found = Object.values(TelemetrySchemaType).find((val: string) => {
                        if (val.toLowerCase() === schemaArray[i].toLowerCase())
                            return val;
                        return undefined
                    })
                    if (found !== undefined)
                        return TelemetrySchemaType[found]
                }
            } else {
                // Complex Schema
                return Ltmi.getSchemaType(schema['@type'])
            }
        } catch (error) {
            Logger.error(`Definition-Worker Failed to get schema from: ${schema} ${error}`)
            return undefined
        }
        Logger.error(`Definition-Worker getSchemaType Undefined: ${schema}`)
        return undefined
    } // getSchemaType()

    private static getSemanticType(type: Array<string> | string): TelemetrySemanticType | undefined {
        // Single Entry, invalid Type, Must be two if Semantic exist
        if (type == undefined || !Array.isArray(type) || !(type.length > 1)) {
            return undefined
        }

        // Iterate and check
        let found: TelemetrySemanticType | undefined = undefined;
        type.forEach(item => {
            const inlist = Object.values(TelemetrySemanticType).find((val: string) => {
                if (val.toLowerCase() === item.toLowerCase())
                    return val;
                return undefined
            })

            if (found === undefined && inlist !== undefined) {
                found = TelemetrySemanticType[inlist]
            }
        }) // forEach type

        return found
    } // getSemanticType()

    private static getUoM(unit: string): TelemetryUnitOfMeasure | undefined {
        if (unit == undefined)
            return undefined

        // Iterate and check
        const inlist = Object.values(TelemetryUnitOfMeasure).find((val: string) => {
            if (val.toLowerCase() === unit.toLowerCase())
                return val;
            return undefined
        })

        if (!inlist && unit.length) {
            Logger.error(`Definition-Worker getUoM Did not find UoM ${unit}`)
        }

        return inlist !== undefined ? TelemetryUnitOfMeasure[inlist] : undefined;
    } // getUoM()

    private static getObjectSchema(schema: any, ltmi: any): Array<any> | undefined {
        let objectArray: any = {}

        schema.fields.forEach((field: any) => {
            objectArray[field.name] = Ltmi.processLtmiTelemetry(field, ltmi)
        })

        return objectArray
    } // getObjectSchema()

    private static getEnumSchema(schema: any): Array<any> {
        let enumArray: any = {}

        schema.enumValues.forEach((val: any) => {
            enumArray[val['enumValue']] = {
                name: val['name'],
                displayName: val['displayName'],
            }
        });

        return enumArray;
    } // getEnumSchema()


}
